<template>
  <b-card
    v-if="data"
    no-body
    class="monthly-orders-card"
  >
    <b-card-header class="monthly-orders-header">
      <h4 class="chart-title mb-0">
        Monthly Orders
      </h4>
      <div class="header-actions">
        <small
          v-if="lastUpdatedAt"
          class="timestamp-badge"
          :class="{ 'clickable': onRefresh }"
          @click="handleRefresh"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="12"
            class="mr-1"
            :class="{ 'spin': isRefreshing }"
          />
          <span v-if="lastRefreshTimeAgo">
            {{ lastRefreshTimeAgo }}
          </span>
          <span v-else>
            {{ __dateTimeFormatterPassedTime(lastUpdatedAt) }}
          </span>
        </small>
      </div>
    </b-card-header>
    <b-card-body class="monthly-orders-body">
      <div class="mo-chart-scroll">
        <div class="mo-chart-wrapper mo-chart-min-width">
          <e-charts
            ref="monthly-orders-chart"
            autoresize
            :options="chartOptions"
            class="mo-chart"
          />
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody,
} from 'bootstrap-vue'
import ECharts from 'vue-echarts'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/grid'
import 'echarts/lib/component/markPoint'
import 'echarts/lib/chart/bar'
import * as echarts from 'echarts'

export default {
  components: {
    'e-charts': ECharts,
    BCard,
    BCardHeader,
    BCardBody,
  },
  props: {
    data: {
      type: Object,
      default: () => {},
    },
    lastUpdatedAt: {
      type: String,
      default: null,
    },
    onRefresh: {
      type: Function,
      default: null,
    },
    isRefreshing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentTime: Date.now(),
      updateInterval: null,
    }
  },
  computed: {
    lastRefreshTimeAgo() {
      try {
        const refreshTimes = JSON.parse(localStorage.getItem('dashboardwidgetsrefreshtimes') || '{}')
        const lastRefreshTime = refreshTimes.monthlyorders
        if (!lastRefreshTime) return null

        const lastRefresh = new Date(lastRefreshTime).getTime()
        const now = this.currentTime
        const diff = now - lastRefresh

        if (diff < 0) return null

        const seconds = Math.floor(diff / 1000)
        const minutes = Math.floor(seconds / 60)
        const hours = Math.floor(minutes / 60)
        const days = Math.floor(hours / 24)
        const months = Math.floor(days / 30)
        const years = Math.floor(months / 12)

        if (minutes < 1) return 'just now'
        if (minutes < 60) return `${minutes} minutes ago`
        if (hours < 24) return `${hours} hours ago`
        if (days < 30) return `${days} days ago`
        if (months < 12) return `${months} months ago`
        return `${years} years ago`
      } catch (e) {
        return null
      }
    },
    monthlyOrders() {
      return this.data?.series?.[0]?.data || []
    },
    monthlyLabels() {
      const categories = this.data?.categories?.lables || []
      const monthMap = {
        January: 'Jan',
        February: 'Feb',
        March: 'Mar',
        April: 'Apr',
        May: 'May',
        June: 'Jun',
        July: 'Jul',
        August: 'Aug',
        September: 'Sep',
        October: 'Oct',
        November: 'Nov',
        December: 'Dec',
      }
      return categories.map(cat => {
        if (typeof cat === 'string') {
          const parts = cat.split(' ')
          if (parts.length >= 2) {
            const month = parts[0]
            const year = parts[1]
            const shortMonth = monthMap[month] || month.substring(0, 3)
            return `${shortMonth}\n${year}`
          }
          return cat
        }
        return cat
      })
    },
    maxValueIndex() {
      const data = this.monthlyOrders
      if (!data || data.length === 0) return -1
      let maxIndex = 0
      let maxValue = data[0] || 0
      data.forEach((value, index) => {
        if (value > maxValue) {
          maxValue = value
          maxIndex = index
        }
      })
      return maxIndex
    },
    maxValue() {
      const data = this.monthlyOrders
      if (!data || data.length === 0) return 0
      return Math.max(...data)
    },
    chartOptions() {
      const data = this.monthlyOrders
      const maxIndex = this.maxValueIndex

      return {
        grid: {
          top: 60,
          left: 40,
          right: 24,
          bottom: 45,
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          backgroundColor: 'transparent',
          borderWidth: 0,
          padding: 0,
          formatter(params) {
            const p = params[0]
            const month = p.axisValue || ''
            const value = p.value || 0

            return `
              <div style="background-color: #007bff; border: 1px solid #0056b3; color: #ffffff; padding: 10px 14px; border-radius: 6px; font-family: 'Roboto', sans-serif; font-size: 11px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15); min-width: 180px;">
                <div style="font-weight: 600; margin-bottom: 8px; line-height: 1.4; font-size: 12px; border-bottom: 1px solid rgba(255, 255, 255, 0.2); padding-bottom: 6px;">Monthly Orders Details</div>
                <div style="margin-bottom: 6px; line-height: 1.5;">
                  <span style="opacity: 0.9; font-weight: 500;">Month:</span>
                  <span style="font-weight: 600; margin-left: 6px;">${month.replace(/\n/g, ' ')}</span>
                </div>
                <div style="margin-top: 8px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.2); line-height: 1.5;">
                  <span style="opacity: 0.9; font-weight: 500;">Count:</span>
                  <span style="font-weight: 600; margin-left: 6px; font-size: 13px;">${value.toLocaleString()}</span>
                </div>
              </div>
            `
          },
          textStyle: {
            fontSize: 11,
            color: '#ffffff',
            fontFamily: 'Roboto, sans-serif',
          },
        },
        xAxis: {
          type: 'category',
          data: this.monthlyLabels,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#6b7280',
            fontSize: 11,
            margin: 14,
            fontFamily: 'Roboto, sans-serif',
            lineHeight: 16,
          },
        },
        yAxis: {
          type: 'value',
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#9ca3af',
            fontSize: 10,
            margin: 8,
            fontFamily: 'Roboto, sans-serif',
            formatter(value) {
              return value.toLocaleString()
            },
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: 'dashed',
              color: '#e5e7eb',
            },
          },
        },
        series: [
          {
            type: 'bar',
            data,
            barWidth: 18,
            itemStyle: {
              borderRadius: [6, 6, 0, 0],
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#1fb0ff' },
                { offset: 1, color: '#007bff' },
              ]),
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 12,
                shadowColor: 'rgba(0, 123, 255, 0.4)',
              },
            },
            label: {
              show: params => params.dataIndex === maxIndex,
              position: 'top',
              formatter: params => params.value.toLocaleString(),
              fontSize: 12,
              fontWeight: 600,
              color: '#5e5873',
              fontFamily: 'Roboto, sans-serif',
              backgroundColor: '#ffffff',
              borderColor: '#e5e7eb',
              borderWidth: 1,
              borderRadius: 8,
              padding: [6, 10],
              shadowBlur: 4,
              shadowColor: 'rgba(0, 0, 0, 0.1)',
              shadowOffsetY: 2,
              distance: 8,
            },
          },
        ],
      }
    },
  },
  mounted() {
    // Update countdown every second
    this.updateInterval = setInterval(() => {
      this.currentTime = Date.now()
    }, 1000)
  },
  beforeDestroy() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
    }
  },
  methods: {
    handleRefresh() {
      if (this.onRefresh && !this.isRefreshing) {
        this.onRefresh()
      }
    },
  },
}
</script>

<style scoped>
.monthly-orders-card {
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  border-radius: 0.857rem;
  overflow: hidden;
  height: 100%;
  transition: all 0.3s ease;
}

.mo-chart-scroll {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
}

.mo-chart-min-width {
  min-width: 500px;
}

.monthly-orders-card:hover {
  box-shadow: 0 8px 32px 0 rgba(34, 41, 47, 0.15);
  transform: translateY(-2px);
}

.monthly-orders-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: #fff;
}

.chart-title {
  font-size: 1.285rem;
  font-weight: 600;
  color: #5e5873;
  margin: 0;
  font-family: 'Roboto', sans-serif;
}

.header-actions {
  display: flex;
  align-items: center;
}

.timestamp-badge {
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  color: #6e6b7b;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s ease;
  font-family: 'Roboto', sans-serif;
}

.timestamp-badge.clickable {
  cursor: pointer;
  user-select: none;
}

.timestamp-badge.clickable:hover {
  background-color: #e8e8e8;
  transform: scale(1.05);
}

.timestamp-badge.clickable:active {
  transform: scale(0.98);
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.monthly-orders-body {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  background: #fff;
}

.mo-chart-wrapper {
  margin-top: 8px;
  height: 270px;
}

.mo-chart {
  width: 100%;
  height: 100%;
}

@media (max-width: 768px) {
  .monthly-orders-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
}
</style>
